import { useCardData } from 'components/providers/CardDataProvider'
import { loadInventory } from 'lib/api'
import { useCallback, useMemo } from 'react'
import {
  Article,
  ArticleParams,
  Card,
  CountryEdition,
  countryEditionMap,
  GRADING_COMPANY_LABELS,
  InventoryArticle,
  INVENTORY_ZOOM_LEVELS,
  mapBackendFinishAttributeToInternal,
  PTInventoryProduct,
} from 'shared'

// The inventory diff endpoint filters on pt_articles.last_pt_transaction_processed_at, so the cursor
// has to come from the data itself: a browser timestamp can sit ahead of the DB clock, and every
// update processed in between would be filtered out of the diff and lost until the next full fetch.
const laterProcessedAt = (current: string | undefined, candidate?: string) =>
  candidate && (!current || candidate > current) ? candidate : current

export const useFetchUserPtInventory = () => {
  const cardData = useCardData()

  const cardDataMap = useMemo(() => {
    const map = new Map<number, Card>()
    for (const card of cardData) {
      map.set(card._id, card)
    }
    return map
  }, [cardData])

  const getAggregatedLocations = (ptInventoryProduct: PTInventoryProduct) => {
    const locations: any[] = []
    ptInventoryProduct.articles.forEach((article) => {
      if (article.locations.length) {
        locations.push(...article.locations)
      } else {
        locations.push(article.locations)
      }
    })
    return locations
  }

  const fetchUserPtInventory = useCallback(
    async (idGame: number, zoomLevel: string, userId?: string) => {
      const rawData = await loadInventory(idGame, userId)

      let maxProcessedAt: string | undefined

      const stock: InventoryArticle[] = []
      for (const d of rawData) {
        if (zoomLevel === INVENTORY_ZOOM_LEVELS.ARTICLE) {
          // this zoom level maps a single row per product out of articles[0], but the cursor still has
          // to cover every article of the product
          for (const a of d.articles) {
            maxProcessedAt = laterProcessedAt(maxProcessedAt, a.lastPtTransactionProcessedAt)
          }

          const attributes: ArticleParams['attributes'] = [
            d.quantity,
            d.condition,
            d.idLanguage,
            Number(d.mkmProductId),
            d.articles[0].baseSellPrice,
            d.articles[0].comment || '',
            false,
            d.isSigned,
            d.isFirstEd,
            '', // idArticle
            null, // listedAt
            false, // isAltered
            GRADING_COMPANY_LABELS.find((entry) => entry.code === d.gradingCompany)?.code ??
              undefined, // grading provider
            d.grade ?? undefined, // grade
            countryEditionMap.find((entry) => entry.code === d.countryEdition)?.value ??
              CountryEdition.Regular,
            mapBackendFinishAttributeToInternal(d.finishType),
            Number(d.articles[0]._id),
          ]

          const article = new Article({
            attributes,
          })
          const card = cardDataMap.get(article.idProduct)
          if (card) {
            stock.push(
              new InventoryArticle({
                attributes,
                card: card.toPlainObject(),
                locations: getAggregatedLocations(d),
                ptProductId: d.articles[0].ptProduct._id,
                ptArticleReservedQuantity: d.reservedQuantity,
                standardTaxationQuantity: d.articles[0].standardTaxationQuantity,
                marginTaxationQuantity: d.articles[0].marginTaxationQuantity,
              })
            )
          }
        } else {
          for (const a of d.articles) {
            maxProcessedAt = laterProcessedAt(maxProcessedAt, a.lastPtTransactionProcessedAt)

            const attributes: ArticleParams['attributes'] = [
              a.quantity,
              a.ptProduct.condition,
              a.ptProduct.idLanguage,
              Number(a.ptProduct.mkmProductId),
              a.baseSellPrice,
              a.comment || '',
              false,
              a.ptProduct.isSigned,
              a.ptProduct.isFirstEd,
              '', // idArticle
              null, // listedAt
              false, // isAltered
              GRADING_COMPANY_LABELS.find((entry) => entry.code === a.ptProduct.gradingCompany)
                ?.code ?? undefined, // grading provider
              a.ptProduct.grade ?? undefined, // grade
              countryEditionMap.find((entry) => entry.code === a.ptProduct.countryEdition)?.value ??
                CountryEdition.Regular,
              mapBackendFinishAttributeToInternal(a.ptProduct.finishType),
              Number(a._id), //ptArticleId
            ]

            const article = new Article({
              attributes,
            })

            const card = cardDataMap.get(article.idProduct)
            if (card) {
              stock.push(
                new InventoryArticle({
                  attributes,
                  card: card.toPlainObject(),
                  locations: a.locations,
                  ptProductId: a.ptProduct._id,
                  ptArticleReservedQuantity: d.reservedQuantity,
                  standardTaxationQuantity: a.standardTaxationQuantity,
                  marginTaxationQuantity: a.marginTaxationQuantity,
                })
              )
            }
          }
        }
      }

      return { rawData, stock, maxProcessedAt }
    },
    [cardData]
  )

  return { fetchUserPtInventory }
}
